import { useState } from 'react';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { Sparkles, Upload, X, Loader } from 'lucide-react';
import { aiService } from '@/services/ai.service';
import { motion, AnimatePresence } from 'framer-motion';

interface AiContentGeneratorProps {
  onContentGenerated: (content: string) => void;
}

export function AiContentGenerator({ onContentGenerated }: AiContentGeneratorProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Imagem muito grande',
        description: 'O tamanho máximo é 5MB',
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setImage(base64);
      setImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        title: 'Digite um prompt',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      const result = await aiService.generateEmailContent({
        prompt,
        image: image || undefined,
      });

      onContentGenerated(result.content);

      toast({
        title: 'Conteúdo gerado com sucesso!',
        variant: 'success',
      });

      // Reset and close
      setPrompt('');
      setImage(null);
      setImagePreview(null);
      setIsOpen(false);
    } catch (error: any) {
      console.error('Error generating content:', error);
      toast({
        title: 'Erro ao gerar conteúdo',
        description: error.response?.data?.message || 'Verifique se a API Key está configurada',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  return (
    <>
      <Button
        type="button"
        onClick={() => setIsOpen(true)}
        variant="outline"
        className="border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10"
      >
        <Sparkles className="w-4 h-4 mr-2" />
        Gerar com IA
      </Button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setIsOpen(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <Card className="bg-brand-ink border-brand-gold/20 p-6 max-w-2xl w-full max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Sparkles className="h-6 w-6 text-brand-gold" />
                    <h2 className="text-2xl font-serif text-brand-gold">
                      Gerar Conteúdo com IA
                    </h2>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-brand-ivory/50 hover:text-brand-gold transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                      Imagem de Referência (opcional)
                    </label>

                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-lg border border-brand-gold/20"
                        />
                        <button
                          onClick={removeImage}
                          className="absolute top-2 right-2 p-2 bg-red-500/80 hover:bg-red-500 rounded-full text-white transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-brand-gold/20 rounded-lg cursor-pointer hover:border-brand-gold/40 transition-colors">
                        <Upload className="w-8 h-8 text-brand-ivory/50 mb-2" />
                        <span className="text-sm text-brand-ivory/60">
                          Clique para fazer upload
                        </span>
                        <span className="text-xs text-brand-ivory/40 mt-1">
                          PNG, JPG até 5MB
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      </label>
                    )}
                  </div>

                  {/* Prompt */}
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                      Descreva o que você quer *
                    </label>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Ex: Crie um email de boas-vindas para novos clientes, com um tom profissional e amigável..."
                      className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                      rows={4}
                    />
                  </div>

                  {/* Info */}
                  <div className="p-4 bg-brand-gold/10 border border-brand-gold/20 rounded-lg">
                    <p className="text-sm text-brand-ivory/80">
                      <strong className="text-brand-gold">Dica:</strong> Seja específico sobre o
                      tom, público-alvo e informações que devem estar no email. Se adicionar uma
                      imagem, a IA vai usá-la como referência visual.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-brand-gold/20">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsOpen(false)}
                      className="flex-1"
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      variant="premium"
                      onClick={handleGenerate}
                      className="flex-1"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Gerar Conteúdo
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
